/**
 * Project detail modal component
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Chip,
  Box,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import GitHubIcon from '@mui/icons-material/GitHub';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CodeIcon from '@mui/icons-material/Code';
import ArticleIcon from '@mui/icons-material/Article';
import { useEffect } from 'react';
import { Project } from '@/types/project';
import { pageColors } from '@/theme/colors';
import { analytics } from '@/lib/analytics';

interface ProjectModalProps {
  project: Project | null;
  open: boolean;
  onClose: () => void;
}

const statusConfig = {
  active: { label: 'Active', color: '#10b981' },
  completed: { label: 'Completed', color: '#3b82f6' },
  archived: { label: 'Archived', color: '#6b7280' },
  'coming-soon': { label: 'Coming Soon', color: '#f59e0b' },
} as const;

const categoryConfig = {
  'open-source': { label: 'Open Source', color: '#06b6d4' },
  personal: { label: 'Personal', color: '#a855f7' },
  experiment: { label: 'Experiment', color: '#ec4899' },
  professional: { label: 'Professional', color: '#3b82f6' },
} as const;

export function ProjectModal({ project, open, onClose }: ProjectModalProps) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const cyan = dark ? pageColors.projects.accent : pageColors.projects.primary;
  const cyanBg = dark ? 'rgba(103, 232, 249, 0.12)' : 'rgba(6, 182, 212, 0.1)';
  const cyanBgHover = dark ? 'rgba(103, 232, 249, 0.2)' : 'rgba(6, 182, 212, 0.2)';

  useEffect(() => {
    if (open && project) {
      analytics.projectViewed(project.id, project.title);
    }
  }, [open, project]);

  if (!project) return null;

  const status = statusConfig[project.status];
  const category = categoryConfig[project.category];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h4" component="div" fontWeight="bold" sx={{ mb: 1 }}>
              {project.title}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={category.label}
                size="small"
                sx={{
                  bgcolor: category.color,
                  color: 'white',
                  fontWeight: 600,
                }}
              />
              <Chip
                label={status.label}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: status.color,
                  color: status.color,
                  fontWeight: 600,
                }}
              />
            </Stack>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{ mt: -1, mr: -1 }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Description */}
          <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
            {project.description}
          </Typography>

          {/* Long Description */}
          {project.longDescription && (
            <>
              <Divider />
              <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-line' }}>
                {project.longDescription}
              </Typography>
            </>
          )}

          {/* Highlights */}
          {project.highlights && project.highlights.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  Key Highlights
                </Typography>
                <List dense>
                  {project.highlights.map((highlight, idx) => (
                    <ListItem key={idx} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon sx={{ color: cyan, fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={highlight}
                        primaryTypographyProps={{
                          variant: 'body2',
                          sx: { lineHeight: 1.6 },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </>
          )}

          {/* Used By */}
          {project.usedBy && project.usedBy.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5 }}>
                  Used By
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {project.usedBy.map((user, idx) => (
                    <Chip
                      key={idx}
                      label={user.name}
                      component="a"
                      href={user.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                      sx={{
                        bgcolor: cyanBg,
                        '&:hover': { bgcolor: cyanBgHover },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {/* Technologies */}
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5 }}>
              Technologies
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {project.tags.map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    bgcolor: cyanBg,
                    color: cyan,
                    fontWeight: 500,
                  }}
                />
              ))}
            </Stack>
          </Box>

          {/* Timeline */}
          {project.startDate && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Timeline
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {new Date(project.startDate).getFullYear()}
                {project.endDate && ` - ${new Date(project.endDate).getFullYear()}`}
                {!project.endDate && project.status === 'active' && ' - Present'}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        {project.githubUrl && (
          <Button
            variant="outlined"
            startIcon={<GitHubIcon />}
            href={project.githubUrl}
            target="_blank"
            rel="noopener noreferrer"
            component="a"
            onClick={() => analytics.projectLinkClicked(project.id, 'github')}
          >
            GitHub
          </Button>
        )}
        {project.npmUrl && (
          <Button
            variant="outlined"
            startIcon={<CodeIcon />}
            href={project.npmUrl}
            target="_blank"
            rel="noopener noreferrer"
            component="a"
            onClick={() => analytics.projectLinkClicked(project.id, 'npm')}
          >
            NPM
          </Button>
        )}
        {project.luaRocksUrl && (
          <Button
            variant="outlined"
            startIcon={<CodeIcon />}
            href={project.luaRocksUrl}
            target="_blank"
            rel="noopener noreferrer"
            component="a"
            onClick={() => analytics.projectLinkClicked(project.id, 'luarocks')}
          >
            LuaRocks
          </Button>
        )}
        {project.demoUrl && (
          <Button
            variant="contained"
            endIcon={<OpenInNewIcon />}
            href={project.demoUrl}
            target="_blank"
            rel="noopener noreferrer"
            component="a"
            onClick={() => analytics.projectLinkClicked(project.id, 'demo')}
            sx={{
              background: pageColors.projects.gradient,
              color: dark ? '#0a0a0a' : 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
              },
            }}
          >
            View Demo
          </Button>
        )}
        {project.blogPostUrl && (
          <Button
            variant="outlined"
            startIcon={<ArticleIcon />}
            href={project.blogPostUrl}
            component="a"
            onClick={() => analytics.projectLinkClicked(project.id, 'blog')}
          >
            Blog Post
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
